/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { AuthBindings } from "@refinedev/core";
import { API_BASE_URL } from "../providers/config";

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface RegisterFormValues {
  email: string;
  password: string;
  acceptTerms: boolean;
}

const authApi = axios.create({
  baseURL: API_BASE_URL || "http://localhost:4000",
  headers: { "Content-Type": "application/json" },
});

export const authProvider: AuthBindings = {
  login: async ({ email, password }: AuthCredentials) => {
    try {
      const response = await authApi.post("/api/auth/login", {
        email,
        password,
      });
      if (response.data?.token) {
        localStorage.setItem("access_token", response.data.token);
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${response.data.token}`;
        console.log("[authProvider.login] Dispatching customAuthChange event.");
        window.dispatchEvent(new CustomEvent("customAuthChange"));
        return { success: true, redirectTo: "/dashboard" };
      }
      throw new Error("No token received");
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.message || "Login failed",
          name: "Invalid credentials",
        },
      };
    }
  },

  register: async ({ email, password, acceptTerms }: RegisterFormValues) => {
    try {
      const response = await authApi.post("/api/auth/register", {
        email,
        password,
        acceptTerms,
      });
      if (response.data?.token) {
        localStorage.setItem("access_token", response.data.token);
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${response.data.token}`;
        console.log(
          "[authProvider.register] Dispatching customAuthChange event.",
        );
        window.dispatchEvent(new CustomEvent("customAuthChange"));
        return { success: true, redirectTo: "/dashboard" };
      }
      throw new Error(response.data?.message || "Registration failed");
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.message || "Registration failed",
          name: "Invalid registration",
        },
      };
    }
  },

  logout: async () => {
    localStorage.removeItem("access_token");
    axios.defaults.headers.common["Authorization"] = "";
    console.log("[authProvider.logout] Dispatching customAuthChange event.");
    window.dispatchEvent(new CustomEvent("customAuthChange"));
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return { authenticated: false };
    try {
      await authApi.get("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { authenticated: true };
    } catch (error) {
      return { authenticated: false };
    }
  },

  onError: async (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      localStorage.removeItem("access_token");
      return { logout: true, redirectTo: "/login" };
    }
    return { error };
  },

  getIdentity: async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    try {
      const response = await authApi.get("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return {
        id: response.data.id,
        name: response.data.name || response.data.email,
        avatar: response.data.avatar,
      };
    } catch (error) {
      return null;
    }
  },
};
